import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { api } from './api';
import type { Judge, Presentation, Score, JudgeSession } from '../shared/types';
import type { Rubric } from '../shared/rubrics';

type Role = 'judge' | 'organizer';

interface SessionState {
  role: Role | null;
  judge: Judge | null;
  allocations: Presentation[];
  scores: Score[];
  rubrics: Rubric[];
  loading: boolean;
  error: string | null;
  login: (credential: { email?: string; passcode?: string }) => Promise<Role>;
  establishSession: (data: { role: Role; token: string; judge?: Judge }) => void;
  logout: () => void;
  refresh: () => Promise<void>;
  applyScore: (score: Score) => void;
}

const Ctx = createContext<SessionState | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [role, setRole] = useState<Role | null>(() => localStorage.getItem('aadpa_role') as Role | null);
  const [judge, setJudge] = useState<Judge | null>(null);
  const [allocations, setAllocations] = useState<Presentation[]>([]);
  const [scores, setScores] = useState<Score[]>([]);
  const [rubrics, setRubrics] = useState<Rubric[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api<JudgeSession>('me');
      setJudge(data.judge);
      setAllocations(data.allocations);
      setScores(data.scores);
      setRubrics(data.rubrics ?? []);
    } catch (e) {
      const err = e as { status?: number; message?: string };
      if (err.status === 401) {
        // token expired/invalid — force re-login
        localStorage.removeItem('aadpa_token');
        localStorage.removeItem('aadpa_role');
        setRole(null);
      }
      setError(err.message ?? 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  // Load judge data when we have a judge session.
  useEffect(() => {
    if (role === 'judge' && localStorage.getItem('aadpa_token')) {
      void refresh();
    }
  }, [role, refresh]);

  const login = useCallback(async (credential: { email?: string; passcode?: string }): Promise<Role> => {
    const data = await api<{ role: Role; token: string; judge?: Judge }>('login', {
      body: credential,
      auth: false,
    });
    establishSession(data);
    return data.role;
  }, []);

  const establishSession = useCallback((data: { role: Role; token: string; judge?: Judge }) => {
    localStorage.setItem('aadpa_token', data.token);
    localStorage.setItem('aadpa_role', data.role);
    setRole(data.role);
    if (data.judge) setJudge(data.judge);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('aadpa_token');
    localStorage.removeItem('aadpa_role');
    setRole(null);
    setJudge(null);
    setAllocations([]);
    setScores([]);
    setRubrics([]);
  }, []);

  const applyScore = useCallback((score: Score) => {
    setScores((prev) => {
      const next = prev.filter((s) => s.presentationId !== score.presentationId);
      next.push(score);
      return next;
    });
  }, []);

  return (
    <Ctx.Provider
      value={{ role, judge, allocations, scores, rubrics, loading, error, login, establishSession, logout, refresh, applyScore }}
    >
      {children}
    </Ctx.Provider>
  );
}

export function useSession(): SessionState {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useSession must be used within SessionProvider');
  return ctx;
}
